import { ensureArray } from '@examdojo/util/ensure-array';
import type { ActionDefinition, ActionResult } from '../../../model/widget-action.model';
import type { ReactionGroup } from '../../../model/reaction.model';

interface CompiledBranch {
  /** Condition thunk — returns truthy (non-zero) when active. null = else branch (always matches). */
  condition: (() => number) | null;
  /** Actions to dispatch when this branch becomes active */
  actions: ActionDefinition[];
}

interface CompiledGroup {
  branches: CompiledBranch[];
  /** Index of the currently active branch (-1 = none) */
  activeIndex: number;
}

/**
 * Evaluates reactive condition groups and dispatches actions
 * when the active branch transitions.
 *
 * Each group is a set of ordered branches (like if/else-if/else).
 * When a group's active branch changes, the new branch's actions are dispatched.
 * A re-entrancy guard prevents infinite loops when reactions dispatch UPDATE_PARAM.
 */
export class ReactionEvaluator {
  constructor(
    reactions: ReactionGroup[],
    compileCondition: (expr: string) => () => number,
    private readonly executeAction: (action: ActionDefinition) => Promise<ActionResult>,
  ) {
    this.groups = reactions.map((group) => ({
      branches: group.match.map((branch) => ({
        condition: branch.else ? null : branch.when ? compileCondition(branch.when) : null,
        actions: ensureArray(branch.dispatch),
      })),
      activeIndex: -1,
    }));
  }

  private readonly groups: CompiledGroup[];
  private evaluating = false;

  /**
   * Evaluate all groups and dispatch actions for any branch transitions.
   * Guarded against re-entrancy — nested calls (from dispatched actions) are ignored.
   */
  async evaluate(): Promise<void> {
    if (this.evaluating) {
      return;
    }

    this.evaluating = true;
    try {
      for (const group of this.groups) {
        const newIndex = this.findActiveBranch(group);

        if (newIndex !== group.activeIndex) {
          group.activeIndex = newIndex;

          if (newIndex >= 0) {
            const branch = group.branches[newIndex];
            for (const action of branch.actions) {
              await this.executeAction(action);
            }
          }
        }
      }
    } finally {
      this.evaluating = false;
    }
  }

  /** Find the first truthy branch index, or -1 if none match */
  private findActiveBranch(group: CompiledGroup): number {
    for (let i = 0; i < group.branches.length; i++) {
      const { condition } = group.branches[i];
      // null condition = else branch (always matches)
      if (condition === null || condition() !== 0) {
        return i;
      }
    }
    return -1;
  }
}
